import { IMidiNote,MidiNote } from './models/midi'
import {IChannelPairDef,Phonia,ModuleRole,IDeviceDef,IModuleDef,DeviceDef, Channel, Device,TRecDeviceDef} from './model'
import { ChannelPort, IArch } from './arch'
import { parse } from 'csv-parse/browser/esm/sync'
import { TagCache, Tag } from './models/tag'
const D = console.log

let module: never

////////////////////////////////////////////////////////////////////////////////
// DEVICE utilities
////////////////////////////////////////////////////////////////////////////////

export class IngestError extends Error {
    _errors: Array<{errors: Array<string>,row?:number}>
    constructor(errors:Array<{errors: Array<string>}>) {
      super('One or more ingestion errors encountered')
      this.name = 'IngestError'
      this._errors = errors
    }
  
    get errors (): Array<{errors: Array<string>,row?:number}> { return this._errors }
}

// RECORD fields (canon)
const Fields = {
    Device:         'DEVICE',
    Product:        'PRODUCT',
    Mfr:            'MFR',
    Module:         'NODE',
    Role:           'NODE TYPE',
    Mode:           'NODE MODE',
    PORT_LF:    'PORT L FROM',
    PORT_LT:      'PORT L TO',
    PORT_RF:    'PORT R FROM',
    PORT_RT:      'PORT R TO',
    Tags:           'NODE TAGS',
    Notes:          'DEVICE NOTES',
    Device_ID:      'DEVICE ID',
    Module_ID:      'NODE ID',
}

/**
 * IMPORT devices from CSV-encoded forn
 * @param idgen 
 * @param data 
 * @returns devices + modules
 */
export function ingest (
        idgen: ()=>string, data:string, arch: IArch) : { devices: Record<string,IDeviceDef> }
{
    const Devices:Record<string,IDeviceDef> = {}
    const Modules:Record<string,IModuleDef> = {}
    const ModulesByID:Record<string,IModuleDef> = {}

    // COLLECT ingestion errors
    const $errors:Array<
        {
            row:number,
            device?: string,
            module?: string,
            errors:string[],
        }> = []


    // MODULE ROLES
    const RoleMap = {
        source:      'source',
        dest:        'dest',
        proc:        'proc',
        s:           'source',
        d:           'dest',
        p:           'proc',
        processor:   'proc',
        destination: 'dest',
    }

    // MODULE MODES
    const ModeMap = {
        mono:   'mono',
        stereo: 'stereo',
        m2s:    'm2s',
        m:      'mono',
        s:      'stereo',
    }

    /**
     * "Touch up" given record.
     * Normalize field names and values.
     * @param rec 
     */
    function normalize (rec:Record<string,string|undefined>) : Record<string,string> {
        const touched = Object
            .entries(rec)
            .map (([field,value]) : string[] => {
                // NORMALIZE field
                field = field.toUpperCase().trim()
                // NORMALIZE value
                value = value?.trim() || ''

                // STRIP CR/LF's from anything but device notes
                if (field !== Fields.Notes) {
                    value = value.replace(/[\r\n]/g, ' ').trim()
                }

                let map: Record<string,string> | undefined
                // NORMALIZE value
                switch (field) {
                    case Fields.Role:
                        map = RoleMap
                        break;
                    case Fields.Mode:
                        map = ModeMap
                        break;
                    // NORMALIZE port values
                    case Fields.PORT_LF:
                    case Fields.PORT_RF:
                    case Fields.PORT_LT:
                    case Fields.PORT_RT:
                        value = value.toUpperCase()
                        break;
                }
                // APPLY value aliases
                value = map ? map[value.toLowerCase()]||value : value
                return [field,value]
            })
            .reduce((p,[field,value]) => {
                p[field] = value
                return p
            },{})

        // ENSURE values of all KNOWN FIELDS are non-null
        Object.values(Fields).forEach(field => {
            if (touched[field] == null) {
                touched[field] = ''
            }
        })
        return touched
    }

    // REQUIRED fields
    const RequiredFields = [
        Fields.Device,
        Fields.Module,
        Fields.Role,
        Fields.Mode,
    ]

    // LOCATE header row (first row containing required fields)
    const {rows,raws} = 
        parse(data,{
            raw: true,
            trim: true,
            relax_column_count: true,
        })
        .reduce((p,{record,raw}) => {
            p.raws = p.raws ?? []
            p.rows = p.rows ?? []
            p.raws.push(raw)
            p.rows.push(record)
            return p
        },{})

    // FIND *LAST* row containing required field names
    const headerRow = 
        rows.findLastIndex((row:string[]) => {
            // NORMALIZE (ostensible) field names
            row = row.map(_ => _?.trim().toUpperCase())
            return RequiredFields.every(field => row.includes(field))
        })

    if (headerRow < 0) {
        throw new IngestError([{
            errors: ['Unable to locate header row']
        }])
    }

    // STRIP all content prior to HEADER
    const records = parse(
        raws.slice(headerRow).join('\n'),
        {
            trim: true,
            columns: true,
            relax_column_count: true,
    })

    records
        .map((rec:Record<string,string|undefined>) => {
            // NORMALIZE fields/values
            rec = normalize(rec)
            // EMPTY row?
            if (!Object.values(Fields).some(field => rec[field])) {
                return
            }
            else {
                return rec
            }
        })
        .forEach((rec:Record<string,string>|undefined,row:number) => {
            // BLANK LINE
            if (!rec) return

            const errors:string[] = []

            ///////////////////////////////////////////////////////////////////////////////////
            // REQUIRED fields
            ///////////////////////////////////////////////////////////////////////////////////

            const dev_name = rec[Fields.Device]
            if (!dev_name) {
                errors.push(`Device display name undefined`)
            }

            const mod_name = rec[Fields.Module]
            if (!mod_name) {
                errors.push(`Module name undefined`)
            }

            const role  = ModuleRole.fromString(rec[Fields.Role])
            if (!role) {
                const $role = rec[Fields.Role]
                if ($role) {
                    errors.push(`Unknown module type '${$role}'`)
                }
                else {
                    errors.push(`Module type (e.g. source) is missing`)
                }
            }

            const mode = Phonia.fromString(rec[Fields.Mode])
            if (!mode) {
                const $mode = rec[Fields.Mode]
                if ($mode) {
                    errors.push(`Unknown module mode '${$mode}'`)
                }
                else {
                    errors.push(`Module mode (e.g. mono) is missing`)
                }
            }

            // VALIDATE and PARSE PORT ID
            function validatePort (port:string) : number | undefined {
                if (!port) return
                if (!ChannelPort.validate(port)) {
                    errors.push(`Malformed port ID '${port}', expected format: ${arch.ports.formatHint()}`)
                    return
                }
                const num = ChannelPort.fromPortId(port, arch)
                if (num === undefined) {
                    errors.push(`Port ID '${port}' is not valid for ${arch.model.name} (expected ${arch.ports.formatHint()})`)
                }
                return num
            }

            ///////////////////////////////////////////////////////////////////////////////////
            // OPTIONAL fields
            ///////////////////////////////////////////////////////////////////////////////////
            const device_id = rec[Fields.Device_ID] || undefined
            const mfr_name  = rec[Fields.Mfr] || undefined
            const prod_name = rec[Fields.Product] || undefined

            const mod_id = rec[Fields.Module_ID] || undefined
            const port_LF = validatePort(rec[Fields.PORT_LF])
            const port_RF = validatePort(rec[Fields.PORT_RF])
            const port_LT = validatePort(rec[Fields.PORT_LT])
            const port_RT = validatePort(rec[Fields.PORT_RT])

            // VALIDATE defined ports are sensical for ROLE + MODE
            if (role && mode) {
                // REMOVE sensical ports, what remains is extraraneous
                const ports = {port_LF, port_RF, port_LT, port_RT}
                if (role?.dest || role?.proc) {
                    delete ports.port_LT
                    if (mode?.stereo) delete ports.port_RT
                }
                if (role?.source || role?.proc) {
                    delete ports.port_LF
                    if (mode?.stereo || mode?.m2s) delete ports.port_RF
                }
                // `ports` should now contain extraneous definitions for given role/mode
                Object
                .entries(ports)
                .forEach(([field,port]) => {
                    const name = Fields[field.toUpperCase()]
                    if (name && port != null) {
                        errors.push(`Port field '${name}' should be empty for module type/mode ${ModuleRole.toString(role)}/${Phonia.toString(mode)}`)
                    }
                })
            }

            const tags = rec[Fields.Tags]
                    .split(/\s*,\s*/)
                    .filter(_ => _.trim())
                    .map(tag => {
                        if (!TagCache.node[tag]) {
                            TagCache.node[tag] = Tag.create(tag)
                        }
                        return TagCache.node[tag]
                    })

            const notes = rec[Fields.Notes] || undefined

            function validateMidi (value:string, pos: 'LEFT'|'RIGHT'|'' = '') : IMidiNote | undefined {
                if (!value) {
                    return
                }
                else {
                    let note: IMidiNote | undefined
                    const [channel,spn] = value.split('|').map(_ => _.trim())
                    if (channel && `${Number.parseInt(channel)}` === channel) {
                        note = MidiNote.fromString({channel:+channel,spn})
                    }
                    if (!note) {
                        errors.push(`BAD MIDI ${pos} TRIGGER '${value}', expected format: CHANNEL|NOTE (CHANNEL: [1...16], NOTE: [C-1...G9])`)
                    }
                    return note
                }
            }

            if (port_LF && port_LF === port_RF) {
                errors.push(`Duplicate port ID for L/R channels: ${ChannelPort.toPortId(port_LF, arch)}`)
            }
            if (port_LT && port_LT === port_RT) {
                errors.push(`Duplicate port ID for L/R channels: ${ChannelPort.toPortId(port_LT, arch)}`)
            }

            ///////////////////////////////////////////////////////////////////////////////////
            // INSTANTIATE device
            ///////////////////////////////////////////////////////////////////////////////////

            if (!errors.length) {
                const device = Devices[dev_name] ||
                    (Devices[dev_name] = {
                        id: device_id || idgen(),
                        name: dev_name,
                        product: {
                            name: prod_name,
                            mfr: mfr_name,
                        },
                        nodes: {},
                        notes,
                    })

                // ASSIGN NOTES (if undefined)
                if (notes && !device.notes) {
                    device.notes = notes
                }

                if (device_id && device.id !== device_id) {
                    errors.push(`Inconsistent IDs (${device_id} vs. ${device.id}) for device ${dev_name}`)
                }
                else if (dev_name !== device.name) {
                    errors.push(`Inconsistent display names (${dev_name} vs. ${device.name}) for device ID ${device.id}`)
                }
            }

            ///////////////////////////////////////////////////////////////////////////////////
            // INSTANTIATE module 
            ///////////////////////////////////////////////////////////////////////////////////
            if (!errors.length && role && mode && mod_name && dev_name ) {
                const device = Devices[dev_name]

                // DERIVE "effective" module ID
                const mod_eff_id = `${dev_name}-${mod_name}-${ModuleRole.toString(role)}`

                if (mod_id && ModulesByID[mod_id]) {
                    const other = ModulesByID[mod_id]
                    errors.push(`Module identity duplication. Module ID ${mod_id} of module ${mod_name} conflicts with module ${other.name} of device ${other.device.name}.`)
                }
                else if (Modules[mod_eff_id]) {
                    const other = Modules[mod_eff_id]
                    errors.push(`Module name/type duplication. Module ${mod_name} name/type ${mod_name}/${ModuleRole.toString(role)} conflicts with other module ${other.name} of device ${other.device.name}.`)
                }
                else {
                    if (!errors.length) {
                        const L: IChannelPairDef =
                            role.source
                            ? {
                                from: port_LF != null ? Channel.create({ from: port_LF, arch }) : undefined
                            }
                            : role.dest
                            ? {
                                to:  port_LT != null ? Channel.create({ to: port_LT, arch }) : undefined
                            }
                            : {
                                from: port_LF != null ? Channel.create({ from: port_LF, arch }) : undefined,
                                to: port_LT != null ? Channel.create({ to: port_LT, arch }) : undefined,
                            }

                        const R: IChannelPairDef =
                            (mode.stereo || mode.m2s)
                            ? (
                                role.source
                                ? {
                                    from: port_RF != null ? Channel.create({ from: port_RF, arch }) : undefined
                                }
                                : role.dest
                                ? {
                                    to: port_RT != null ? Channel.create({ to: port_RT, arch }) : undefined
                                }
                                : {
                                    from: port_RF != null ? Channel.create({ from: port_RF, arch }) : undefined,
                                    // M2S module requires a single mono "input"
                                    to: port_RT != null && !mode.m2s ? Channel.create({ to: port_RT, arch }) : undefined,
                                }
                            )
                            : {}

                        // INDEX by "effective" ID
                        // (detect module name/type conflicts within device)
                        Modules[mod_eff_id] = {
                            id: mod_id || idgen(),
                            name: mod_name!,
                            device: Device.fromDef(device),
                            role,
                            phonia: mode,
                            tags,
                            L,R,
                        }

                        // INDEX by OBJECT ID
                        // (detect module identify conflicts)
                        ModulesByID[Modules[mod_eff_id].id] = Modules[mod_eff_id] 
                    }
                }
            }

            if (errors.length) {
                $errors.push({
                    row: row + headerRow,
                    module: mod_name,
                    device: dev_name,
                    errors
                })
            }
        })

    // INDEX device instances by ID
    const devices: TRecDeviceDef = Object
        .values(Devices)
        .reduce((p,dev) => {
            p[dev.id] = dev
            return p
        },{})

    // ASSIGN nodes to respective devices
    Object
        .values(Modules)
        .forEach(node => {
            const device = devices[node.device.id]
            if (device) {
                device.nodes[node.id] = node
            }
    })

    if ($errors.length) {
        D('IMPORT ERRORS',$errors)
        // TOUCH up row values (off by 1)
        throw new IngestError(
            $errors
                .map(({row,errors}) => ({
                    row: Array.isArray(row) ? row.map(_ => _ + 1) : row + 1,
                    errors,
                }))
        )
    }
    return { devices }
}

/**
 * EXPORT devices to CSV-encoded forn
 * @param devices
 * @returns CSV-encoded export
 */
export function excrete (devices: IDeviceDef[], arch: IArch) : string
{
    const Devices = {}

    function toPortID (port?:number) : string | undefined {
        if (port != null) {
            return ChannelPort.toPortId(port, arch)
        }
    }

    return DeviceDef.extractNodes(devices)
        .map(mod => {
            // ENCOUNTERED first instance of device
            const first = !Devices[mod.device.id]
            const dev = Devices[mod.device.id] = mod.device
            return {
                [Fields.Mfr]: dev.product.mfr,
                [Fields.Product]: dev.product.name,
                [Fields.Device]: dev.name,
                [Fields.Module]: mod.name,
                [Fields.Role]: ModuleRole.toString(mod.role),
                [Fields.Mode]: Phonia.toString(mod.phonia),
                [Fields.PORT_LF]: toPortID(mod.L.from?.port),
                [Fields.PORT_RF]: toPortID(mod.R.from?.port),
                [Fields.PORT_LT]: toPortID(mod.L.to?.port),
                [Fields.PORT_RT]: toPortID(mod.R.to?.port),
                [Fields.Tags]: mod.tags.map(_ => _.name).join(','),
                // ONLY include notes once
                [Fields.Notes]: first && dev.notes ? dev.notes : undefined,
                [Fields.Device_ID]: dev.id,
                [Fields.Module_ID]: mod.id,
            }
        })
        // MAKE everything CSV-safe
        .reduce((rows:string[],rec:any,i) => {
            function csvEsc(value:string='') : string {
                return (/[",\n]/.test(value))
                    ? `"${value.replaceAll('"', '""')}"`
                    : value
            }
            // HEADER ROW
            if (i === 0) {
                rows.push(Object.keys(rec).join(','))
            }
            rows.push(
                Object
                    .entries(rec)
                    .map(([_,v]) => csvEsc(v as string))
                    .join(',')
            )
            return rows
        },[])
        .join("\n")
}

/**
 * CSV-encoded "demo" devices
 */
export const DemoDevices = (idgen, arch: IArch) => ingest(idgen,
    `
THIS IS BLANK LEADER

MFR,PRODUCT,DEVICE,NODE,NODE TYPE,NODE MODE,PORT L FROM,PORT R FROM,PORT L TO,PORT R TO,NODE TAGS,MIDI,MIDI L,MIDI R,DEVICE NOTES,DEVICE ID,NODE ID
,,Stereo Source,1,Source,Stereo,B1,B2,,,misc,A0,,,Some notes,ID_0,ID_1
,,Stereo Source,2,Source,Stereo,B3,B4,,,misc,,,,,ID_0,ID_4
,,Stereo Source,3,Source,Stereo,B5,B6,,,misc,,,,,ID_0,ID_7
,,Stereo Source,4,Source,Stereo,B7,B8,,,misc,,,,,ID_0,ID_A
,,Stereo Dest,1,Destination,Stereo,,,F1,F2,misc,,,,,ID_D,ID_E
,,Stereo Dest,2,Destination,Stereo,,,F3,F4,misc,,,,,ID_D,ID_H
,,Stereo Dest,3,Destination,Stereo,,,F5,F6,misc,,,,,ID_D,ID_K
,,Stereo Dest,4,Destination,Stereo,,,F7,F8,misc,,,,,ID_D,ID_N
,,MicBox A,1,Source,Mono,A1,,,,"snake,mic",A1,,,,ID_Q,ID_R
,,MicBox A,2,Source,Mono,A2,,,,"snake,mic",,,,,ID_Q,ID_T
,,MicBox A,3,Source,Mono,A3,,,,"snake,mic",,,,,ID_Q,ID_V
,,MicBox A,4,Source,Mono,A3,,,,"snake,mic",,,,,ID_Q,ID_X
,,MicBox A,5,Source,Mono,A5,,,,"snake,mic",,,,,ID_Q,ID_Z
,,MicBox A,6,Source,Mono,A6,,,,"snake,mic",,,,,ID_Q,ID_11
,,MicBox A,7,Source,Mono,A7,,,,"snake,mic",,,,,ID_Q,ID_13
,,MicBox A,8,Source,Mono,A8,,,,"snake,mic",,,,,ID_Q,ID_15
,,Heritage Britstrip 1,misc,Processor,Mono,A1,,C1,,"misc,preamp",,,,,ID_17,ID_18
,,Heritage Britstrip 2,misc,Processor,Mono,A2,,C2,,"misc,preamp",,,,,ID_1B,ID_1C
,,Distressor 1,compressor,Processor,Mono,A3,,C3,,compressor,,,,,ID_1F,ID_1G
,,Distressor 2,compressor,Processor,Mono,A3,,C4,,compressor,,,,,ID_1J,ID_1K
,,API 3124mv,1,Processor,Mono,A5,,C5,,micpre,,,,,ID_1N,ID_1O
,,API 3124mv,2,Processor,Mono,A6,,C6,,micpre,,,,,ID_1N,ID_1R
,,API 3124mv,3,Processor,Mono,A7,,C7,,micpre,,,,,ID_1N,ID_1U
,,API 3124mv,4,Processor,Mono,A8,,C8,,micpre,,,,,ID_1N,ID_1X
,,V3A 1,compressor,Processor,Mono,B1,,D1,,compressor,,,,,ID_20,ID_21
,,V3A 2,compressor,Processor,Mono,B2,,D2,,compressor,,,,,ID_24,ID_25
,,Silver Bullet,1,Processor,Stereo,B3,B4,D3,D4,compressor,,,,,ID_28,ID_29
,,Silver Bullet,2,Processor,Stereo,D1,D2,E6,E7,compressor,,,,,ID_28,ID_2E
,,Silver Bullet,Mic Pre 1,Destination,Mono,,,C7,,compressor,,,,,ID_28,ID_2J
,,Silver Bullet,Mic Pre 2,Destination,Mono,,,C8,,compressor,,,,,ID_28,ID_2L
,,Shelford 1,micpre,Processor,Mono,B5,,D5,,micpre,,,,,ID_2N,ID_2O
,,Shelford 2,micpre,Processor,Mono,B6,,D6,,micpre,,,,,ID_2R,ID_2S
Comp,TubeTech,Tubetech,compressor,Processor,Mono,C6,,D7,,compressor,,,,,ID_2V,ID_2W
,,s500 1,out,Source,Mono,D8,,,,micpre,,,,,ID_2Z,ID_30
,,s500 2,out,Source,Mono,E1,,,,micpre,,,,,ID_32,ID_33
,,s500 3,out,Source,Mono,E2,,,,micpre,,,,,ID_35,ID_36
,,s500 4,out,Source,Mono,E3,,,,micpre,,,,,ID_38,ID_39
,,s500 5,out,Source,Mono,E4,,,,micpre,,,,,ID_3B,ID_3C
,,s500 6,out,Source,Mono,E5,,,,micpre,,,,,ID_3E,ID_3F
,,Backpanel In,E8,Source,Mono,E8,,,,patchbay,,,,,ID_3H,ID_3I
,,MTRX Mic In 1,in,Destination,Mono,,,B7,,micpre,,,,,ID_3K,ID_3L
,,MTRX Mic In 2,in,Destination,Mono,,,B8,,micpre,,,,,ID_3N,ID_3O
,,Backpanel Out,D3,Destination,Mono,,,D3,,patchbay,,,,,ID_3Q,ID_3R
,,Backpanel Out,D4,Destination,Mono,,,D4,,patchbay,,,,,ID_3Q,ID_3T
,,Backpanel Out,D5,Destination,Mono,,,D5,,patchbay,,,,,ID_3Q,ID_3V
,,Backpanel Out,D6,Destination,Mono,,,D6,,patchbay,,,,,ID_3Q,ID_3X
,,Backpanel Out,D7,Destination,Mono,,,D7,,patchbay,,,,,ID_3Q,ID_3Z
,,Backpanel Out,D8,Destination,Mono,,,D8,,patchbay,,,,,ID_3Q,ID_41
,,MTRX Line In,1,Destination,Mono,,,E1,,adda,,,,,ID_43,ID_44
,,MTRX Line In,2,Destination,Mono,,,E2,,adda,,,,,ID_43,ID_46
,,MTRX Line In,3,Destination,Mono,,,E3,,adda,,,,,ID_43,ID_48
,,MTRX Line In,4,Destination,Mono,,,E4,,adda,,,,,ID_43,ID_4A
,,MTRX Line In,5,Destination,Mono,,,E5,,adda,,,,,ID_43,ID_4C
,,MTRX Line In,6,Destination,Mono,,,E6,,adda,,,,,ID_43,ID_4E
,,MTRX Line In,7,Destination,Mono,,,E7,,adda,,,,,ID_43,ID_4G
,,MTRX Line In,8,Destination,Mono,,,E8,,adda,,,,,ID_43,ID_4I
,,M2S,1,Processor,M2S,TT1,TT2,TT1,,chorus,,,,,M6ITW3DT_6,M6ITW3DT_B
,,M2S,2,Processor,M2S,TT3,TT4,TT2,,chorus,,,,,M6ITW3DT_6,M6ITW3DT_F
,,M3S,3,Processor,M2S,TT5,TT6,TT3,,chorus,,,,,M6ITW3DT_7,M6ITW3DT_J
,,Stereo Proc 1,1,Processor,Stereo,TT7,TT8,TT4,TT5,"compressor,delay",A4,,,,M6ITW3DT_8,M6ITW3DT_N
,,Stereo Proc 2,2,Processor,Stereo,TT11,TT12,TT9,TT10,compressor,,,,,M6ITW3DT_9,M6ITW3DT_S
,,Stereo Proc 3,3,Processor,Stereo,TT15,TT16,TT13,TT14,compressor,,,,,M6ITW3DT_A,M6ITW3DT_X


`, arch)
